// Procedural painting surfaces: papers, canvases, boards and smooth digital.
//
// A surface provides, per simulation cell:
//   heightField 0..1 relief (valleys collect granulating pigment; peaks catch dry brush)
//   absorbency  multiplier on how fast surface water soaks into the surface
//   capillary   multiplier on how fast absorbed water wicks sideways (fibre conduction)
//   pinning     extra depth surface water needs before it can advance onto dry cells
// and, per surface (preset), physical constants used by the simulation:
//   tooth (relief amplitude), friction (dry-brush contact), drying (evaporation
//   multiplier), sheen (base surface gloss for lighting).
// Relief is built from several independent scales of value noise plus
// structure (fibres, laid lines, weave, wood grain), so it does not repeat.
// Generators are deterministic from a seed. All presets are calibrated
// artistic approximations, not reproductions of commercial products.

import { createRng, hash2 } from '../core/rng.js';
import { SURFACE_LIBRARY, resolveSurfaceSettings } from './SurfaceLibrary.js';

export { SURFACE_LIBRARY };

// Back-compatible name used by the Lab and saved documents (id -> preset).
export const SURFACE_PRESETS = SURFACE_LIBRARY;

function smooth(t) {
  return t * t * (3 - 2 * t);
}

function valueNoise(x, y, seed) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const tx = smooth(x - xi);
  const ty = smooth(y - yi);
  const a = hash2(xi, yi, seed);
  const b = hash2(xi + 1, yi, seed);
  const c = hash2(xi, yi + 1, seed);
  const d = hash2(xi + 1, yi + 1, seed);
  return a + (b - a) * tx + (c - a) * ty + (a - b - c + d) * tx * ty;
}

function fractalNoise(x, y, seed, octaves = 4) {
  let sum = 0;
  let amplitude = 1;
  let frequency = 1;
  let norm = 0;
  for (let o = 0; o < octaves; o += 1) {
    // Each octave is rotated a little so lattice axes do not line up.
    const ca = Math.cos(o * 0.7);
    const sa = Math.sin(o * 0.7);
    const u = (x * ca - y * sa) * frequency;
    const v = (x * sa + y * ca) * frequency;
    sum += amplitude * valueNoise(u, v, seed + o * 101);
    norm += amplitude;
    amplitude *= 0.5;
    frequency *= 2.03;
  }
  return sum / norm;
}

function boxBlur(src, width, height, radius) {
  if (radius < 1) return src;
  const tmp = new Float32Array(src.length);
  const out = new Float32Array(src.length);
  const span = radius * 2 + 1;
  for (let y = 0; y < height; y += 1) {
    let acc = 0;
    for (let x = -radius; x <= radius; x += 1) acc += src[y * width + Math.min(width - 1, Math.max(0, x))];
    for (let x = 0; x < width; x += 1) {
      tmp[y * width + x] = acc / span;
      acc += src[y * width + Math.min(width - 1, x + radius + 1)] - src[y * width + Math.max(0, x - radius)];
    }
  }
  for (let x = 0; x < width; x += 1) {
    let acc = 0;
    for (let y = -radius; y <= radius; y += 1) acc += tmp[Math.min(height - 1, Math.max(0, y)) * width + x];
    for (let y = 0; y < height; y += 1) {
      out[y * width + x] = acc / span;
      acc += tmp[Math.min(height - 1, y + radius + 1) * width + x] - tmp[Math.max(0, y - radius) * width + x];
    }
  }
  return out;
}

function normaliseInPlace(field) {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < field.length; i += 1) {
    if (field[i] < min) min = field[i];
    if (field[i] > max) max = field[i];
  }
  const span = max - min;
  if (!(span > 1e-9)) {
    field.fill(0.5);
    return;
  }
  for (let i = 0; i < field.length; i += 1) field[i] = (field[i] - min) / span;
}

// Randomly oriented, gently curving fibres; returns a fibre-density field.
function fibreField(width, height, seed, density, length = 1, grainAngle = null) {
  const field = new Float32Array(width * height);
  if (density <= 0) return field;
  const rng = createRng(seed ^ 0x51f1b3);
  const count = Math.round((width * height) / 28 * density);
  for (let f = 0; f < count; f += 1) {
    let x = rng.next() * width;
    let y = rng.next() * height;
    // Machine-made paper aligns fibres loosely with the grain direction.
    let angle = grainAngle === null ? rng.next() * Math.PI * 2 : grainAngle + (rng.next() - 0.5) * 1.6;
    const fibreLength = (6 + rng.next() * 22) * length;
    const bend = (rng.next() - 0.5) * 0.25;
    const weight = 0.4 + rng.next() * 0.6;
    for (let s = 0; s < fibreLength; s += 0.7) {
      const xi = Math.floor(x);
      const yi = Math.floor(y);
      if (xi >= 0 && yi >= 0 && xi < width && yi < height) field[yi * width + xi] += weight;
      x += Math.cos(angle) * 0.7;
      y += Math.sin(angle) * 0.7;
      angle += bend * 0.7;
    }
  }
  return field;
}

// Plain weave: alternating warp/weft threads with slub irregularity.
function weaveRelief(u, v, p, irregular, seed) {
  const su = u / p + irregular * (valueNoise(u * 0.04, v * 0.04, seed + 9) - 0.5);
  const sv = v / p + irregular * (valueNoise(u * 0.04 + 30, v * 0.04, seed + 9) - 0.5);
  const parity = (Math.floor(su) + Math.floor(sv)) & 1;
  const warp = Math.sin(Math.PI * (su - Math.floor(su)));
  const weft = Math.sin(Math.PI * (sv - Math.floor(sv)));
  // Slubs: threads vary in thickness along their length.
  const slubU = 0.85 + 0.3 * valueNoise(Math.floor(su) * 3.1, sv * 0.3, seed + 19);
  const slubV = 0.85 + 0.3 * valueNoise(su * 0.3, Math.floor(sv) * 3.1, seed + 23);
  return parity ? warp * slubU * (0.6 + 0.4 * weft) : weft * slubV * (0.6 + 0.4 * warp);
}

export function createSurface(width, height, presetId = 'cold_press', seed = 7, overrides = {}) {
  const preset = resolveSurfaceSettings(presetId, overrides);
  const n = width * height;
  const heightField = new Float32Array(n);
  const absorbency = new Float32Array(n);
  const capillary = new Float32Array(n);
  const pinning = new Float32Array(n);
  const scale = Math.max(0.1, preset.textureScale || 1);
  const rot = ((preset.textureRotation || 0) * Math.PI) / 180;
  const cr = Math.cos(rot);
  const sr = Math.sin(rot);

  if (preset.kind === 'digital') {
    heightField.fill(0.5);
    absorbency.fill(preset.absorbency);
    capillary.fill(preset.capillary);
    pinning.fill(preset.sizing);
    return { width, height, preset, seed, heightField, absorbency, capillary, pinning };
  }

  const grainAngle = preset.grainDirection === null || preset.grainDirection === undefined ? null : (preset.grainDirection * Math.PI) / 180 + rot;
  const fibres = boxBlur(fibreField(width, height, seed, preset.fibre, preset.fibreLength || 1, grainAngle), width, height, 1);
  normaliseInPlace(fibres);
  const toothScale = 1 / Math.max(1, preset.toothScale * scale);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = y * width + x;
      // Rotated, scaled texture coordinates.
      const u = (x * cr - y * sr) / scale;
      const v = (x * sr + y * cr) / scale;
      let tooth = fractalNoise(u * toothScale * scale, v * toothScale * scale, seed, 4);
      tooth = smooth(Math.min(1, Math.max(0, (tooth - 0.2) / 0.6)));
      // Medium scale undulation (cockling, felt marks) plus fine grain.
      const medium = fractalNoise(u * 0.012, v * 0.012, seed + 41, 3);
      const fine = valueNoise(u * 0.9, v * 0.9, seed + 57);
      let relief = preset.tooth * tooth + (1 - preset.tooth) * 0.5;
      relief += preset.fineGrain * (fine - 0.5);
      relief += preset.undulation * (medium - 0.5);
      if (preset.handmade > 0) {
        // Uneven pulp: cloudy thickness variation and occasional inclusions.
        const cloud = fractalNoise(u * 0.035, v * 0.035, seed + 71, 4);
        const inclusion = hash2(Math.floor(u / 9), Math.floor(v / 9), seed + 73) > 0.985 ? 0.35 : 0;
        relief += preset.handmade * (0.6 * (cloud - 0.5) + inclusion);
      }
      if (preset.laid > 0) {
        // Laid lines and chain lines of a mould-made pastel paper.
        const laid = Math.sin((v / (preset.laidSpacing || 2.5)) * Math.PI);
        const chainOffset = (((u % 40) + 40) % 40) - 20;
        const chain = Math.exp(-(chainOffset * chainOffset) / 6);
        relief += preset.laid * (0.25 * laid * laid + 0.2 * chain);
      }
      if (preset.kind === 'canvas') {
        const thread = weaveRelief(u, v, preset.weave, preset.weaveIrregularity ?? 0.15, seed);
        relief = preset.weaveDepth * thread + (1 - preset.weaveDepth) * relief;
      }
      if (preset.kind === 'wood') {
        // Wood grain: ring lines distorted by low-frequency noise, under gesso.
        const warp = fractalNoise(u * 0.01, v * 0.004, seed + 91, 3);
        const rings = Math.sin((v * 0.09 + warp * 8) * Math.PI);
        relief += preset.woodGrain * 0.5 * rings;
      }
      relief = 0.85 * relief + 0.15 * fibres[i] * preset.fibre * 2;
      heightField[i] = relief;
      const fibre = fibres[i];
      const pulp = preset.handmade > 0 ? 0.8 + 0.4 * fractalNoise(u * 0.05, v * 0.05, seed + 79, 2) : 1;
      absorbency[i] = preset.absorbency * pulp * (0.75 + 0.5 * fibre) * (0.85 + 0.3 * hash2(x, y, seed + 3));
      capillary[i] = preset.capillary * (0.5 + 1.0 * fibre);
      pinning[i] = preset.sizing * (0.6 + 0.8 * hash2(x >> 1, y >> 1, seed + 5)) * (1.2 - 0.4 * fibre);
    }
  }
  normaliseInPlace(heightField);
  // Grain depth compresses relief toward the mid plane (0 = flat).
  const depth = Math.max(0, Math.min(1.5, preset.grainDepth ?? 1));
  if (depth !== 1) for (let i = 0; i < n; i += 1) heightField[i] = 0.5 + (heightField[i] - 0.5) * depth;
  return { width, height, preset, seed, heightField, absorbency, capillary, pinning };
}

// Physics view of a surface. In 'visual' mode the paint behaves as on smooth
// digital paper; in 'physical' mode the surface acts with the given strength.
export function physicalSurfaceOf(surface, mode = 'physical', strength = 1) {
  const s = mode === 'visual' ? 0 : Math.max(0, Math.min(1, strength));
  if (s === 1) return surface;
  const n = surface.heightField.length;
  const heightField = new Float32Array(n);
  const absorbency = new Float32Array(n);
  const capillary = new Float32Array(n);
  const pinning = new Float32Array(n);
  const digital = resolveSurfaceSettings('smooth_digital');
  for (let i = 0; i < n; i += 1) {
    heightField[i] = 0.5 + (surface.heightField[i] - 0.5) * s;
    absorbency[i] = digital.absorbency + (surface.absorbency[i] - digital.absorbency) * s;
    capillary[i] = digital.capillary + (surface.capillary[i] - digital.capillary) * s;
    pinning[i] = digital.sizing + (surface.pinning[i] - digital.sizing) * s;
  }
  const preset = {
    ...surface.preset,
    tooth: surface.preset.tooth * s,
    friction: (surface.preset.friction ?? surface.preset.tooth) * s,
    drying: 1 + ((surface.preset.drying ?? 1) - 1) * s,
  };
  return { ...surface, preset, heightField, absorbency, capillary, pinning };
}
